<template>
  <div v-if="page_loading">
    <loading :page_loading="page_loading" />
  </div>
  <div v-else>
    <loading :loading="loading" />

    <!-- นำทาง -->
    <v-card class="v-card-bottom-30">
      <v-card-title>
        {{ use_language.oms }}
        <v-icon>mdi-chevron-right</v-icon>
        {{ use_language.quota_management }}
      </v-card-title>
    </v-card>

    <oms-import-dialog
      v-model="importDialog"
      :title="use_language.import_excel + ' : ' + use_language.quota"
      :hint="use_language.import_hint_quota"
      :columns="importColumns"
      :template-rows="importTemplateRows"
      template-name="oms_quota_template"
      :import-fn="importQuota"
      @done="afterImport()"
    />

    <!-- เตือนเมื่อโควตาคงเหลือรวมมากกว่า stock พร้อมขาย -->
    <v-alert v-if="overAllocatedCount > 0" type="warning" dense text class="v-card-bottom-30">
      {{ use_language.over_allocated_warning }} {{ formatMoney(String(overAllocatedCount)) }} {{ use_language.items }}
      <a class="ml-2" @click="showOverAllocated()">{{ use_language.quota_summary }}</a>
    </v-alert>

    <v-card elevation="1" class="v-card-bottom-30">
      <v-card-title class="height-57">
        <v-icon size="22" color="success darken-3">mdi-chart-pie</v-icon><span class="ml-3">{{ use_language.quota }}</span>
        <v-spacer/>
        <v-btn :elevation="1" outlined color="success" class="px-2" @click="importDialog = true">
          <v-icon size="20" class="mr-1">mdi-file-excel</v-icon>
          {{ use_language.import_excel }}
        </v-btn>
      </v-card-title>
      <v-divider/>
      <v-card-text>
        <v-tabs v-model="tab" @change="onTabChange()">
          <v-tab>{{ use_language.quota_by_location }}</v-tab>
          <v-tab>{{ use_language.quota_summary }}</v-tab>
        </v-tabs>

        <!-- ================= โควตาตาม Location ================= -->
        <div v-show="tab == 0" class="pt-3">

          <!-- ชั้นที่ 1: รายการ location (location มีหลายร้อย จึงไม่แสดงสินค้าของทุก location พร้อมกัน) -->
          <div v-if="current == null">
            <v-row>
              <v-col cols="12" md="3" class="pa-1">
                <v-select v-model="locationFilter.channel" :items="channelItems" :label="use_language.channel" outlined dense hide-details></v-select>
              </v-col>
              <v-col cols="12" md="5" class="pa-1">
                <v-text-field
                  v-model="locationFilter.keyword"
                  :placeholder="use_language.search + ' ' + use_language.location"
                  prepend-inner-icon="mdi-magnify"
                  autocomplete="off"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>

            <v-data-table
              :headers="locationHeader"
              :items="filteredLocations"
              :items-per-page="50"
              :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
              :no-data-text="use_language.no_data"
              class="packhai-border-table mt-3"
              @click:row="item => openLocation(item)"
            >
              <template v-slot:item.no="{ item }">{{ filteredLocations.indexOf(item) + 1 }}</template>
              <template v-slot:item.locationName="{ item }">
                <a>{{ item.locationName }}</a>
              </template>
              <template v-slot:item.productCount="{ item }">{{ formatMoney(String(item.productCount)) }}</template>
              <template v-slot:item.quotaQty="{ item }">{{ formatMoney(String(item.quotaQty)) }}</template>
              <template v-slot:item.reservedQty="{ item }">{{ formatMoney(String(item.reservedQty)) }}</template>
              <template v-slot:item.usedQty="{ item }">{{ formatMoney(String(item.usedQty)) }}</template>
              <template v-slot:item.availableQty="{ item }"><b>{{ formatMoney(String(item.availableQty)) }}</b></template>
              <template v-slot:item.detail="{}">
                <a><v-icon size="18">fa-edit</v-icon></a>
              </template>
            </v-data-table>
          </div>

          <!-- ชั้นที่ 2: สินค้าและโควตาของ location ที่เลือก -->
          <div v-else>
            <v-row align="center">
              <v-col cols="12" class="pa-1 d-flex align-center">
                <v-btn :elevation="1" outlined class="px-2 mr-3" @click="backToLocations()">
                  <v-icon size="20" class="mr-1">mdi-arrow-left</v-icon>
                  {{ use_language.back }}
                </v-btn>
                <span class="fn-14"><b>{{ current.channel }} : {{ current.locationName }}</b></span>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="5" class="pa-1">
                <v-text-field
                  v-model="quota.keyword"
                  :placeholder="use_language.search + ' ' + use_language.product + ' / ' + use_language.sku"
                  prepend-inner-icon="mdi-magnify"
                  autocomplete="off"
                  outlined
                  dense
                  hide-details
                  clearable
                  @keyup.enter="searchQuota()"
                  @click:clear="quota.keyword = null; searchQuota()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="7" class="pa-1 d-flex align-center">
                <v-checkbox v-model="quota.onlyAllocated" :label="use_language.only_allocated" hide-details class="mt-0" @change="searchQuota()"></v-checkbox>
                <v-spacer/>
                <v-btn :elevation="1" color="primary" class="px-2" @click="searchQuota()">{{ use_language.search }}</v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="quotaHeader"
              :items="quota.items"
              :items-per-page="quota.take"
              hide-default-footer
              :no-data-text="use_language.no_data"
              class="packhai-border-table mt-3"
            >
              <template v-slot:item.photoLink="{ item }">
                <v-img v-if="item.photoLink" :src="item.photoLink" width="44" height="44" contain class="my-1 mx-auto"></v-img>
              </template>
              <template v-slot:item.productName="{ item }">
                <div>{{ item.productName }}</div>
                <div style="font-size: 12px; color: #9e9e9e;">{{ item.sku }}<span v-if="item.warehouseName"> · {{ item.warehouseName }}</span></div>
                <div v-if="problems[item.stockShopID]" style="font-size: 12px; color: red;">{{ problems[item.stockShopID] }}</div>
              </template>
              <template v-slot:item.stockQty="{ item }">{{ formatMoney(String(item.stockQty)) }}</template>
              <!-- ยังไม่จัดสรร = stock พร้อมขาย หักโควตาคงเหลือรวมทุก location (ปรับตามตัวเลขที่กำลังแก้) -->
              <template v-slot:item.unallocatedQty="{ item }">
                <span v-if="unallocatedOf(item) < 0" style="color: red;"><b>{{ use_language.over_allocated }} {{ formatMoney(String(-unallocatedOf(item))) }}</b></span>
                <span v-else>{{ formatMoney(String(unallocatedOf(item))) }}</span>
              </template>
              <template v-slot:item.quotaQty="{ item }">
                <v-text-field
                  :value="edits[item.stockShopID] != null ? edits[item.stockShopID] : item.quotaQty"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details
                  class="my-1"
                  :background-color="edits[item.stockShopID] != null ? '#FFF8E1' : ''"
                  :error="problems[item.stockShopID] != null"
                  @input="value => setEdit(item, value)"
                ></v-text-field>
              </template>
              <template v-slot:item.reservedQty="{ item }">{{ formatMoney(String(item.reservedQty)) }}</template>
              <template v-slot:item.usedQty="{ item }">{{ formatMoney(String(item.usedQty)) }}</template>
              <template v-slot:item.availableQty="{ item }">
                <b>{{ formatMoney(String(availableOf(item))) }}</b>
              </template>
            </v-data-table>

            <v-row class="pt-3">
              <v-col cols="12" md="8" class="pa-1">
                <v-pagination
                  v-if="quotaPageCount > 1"
                  v-model="quota.page"
                  :length="quotaPageCount"
                  :total-visible="7"
                  @input="loadQuota()"
                ></v-pagination>
              </v-col>
              <v-col cols="12" md="4" class="pa-1 text-right">
                <span v-if="editCount > 0" class="mr-3">{{ use_language.changed }} {{ formatMoney(String(editCount)) }} {{ use_language.items }}</span>
                <v-btn :elevation="1" color="primary" class="px-2" :disabled="editCount == 0" @click="SaveQuota()">
                  <v-icon size="20" class="mr-1">mdi-content-save</v-icon>
                  {{ use_language.save_changes }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- ================= สรุปเทียบ stock ================= -->
        <div v-show="tab == 1" class="pt-3">
          <v-row>
            <v-col cols="12" md="5" class="pa-1">
              <v-text-field
                v-model="summary.keyword"
                :placeholder="use_language.search + ' ' + use_language.product + ' / ' + use_language.sku"
                prepend-inner-icon="mdi-magnify"
                autocomplete="off"
                outlined
                dense
                hide-details
                clearable
                @keyup.enter="searchSummary()"
                @click:clear="summary.keyword = null; searchSummary()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="7" class="pa-1 d-flex align-center">
              <v-checkbox v-model="summary.onlyOverAllocated" :label="use_language.only_over_allocated" hide-details class="mt-0" @change="searchSummary()"></v-checkbox>
              <v-spacer/>
              <v-btn :elevation="1" color="primary" class="px-2" @click="searchSummary()">{{ use_language.search }}</v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="summaryHeader"
            :items="summary.items"
            :items-per-page="summary.take"
            hide-default-footer
            :no-data-text="use_language.no_data"
            class="packhai-border-table mt-3"
          >
            <template v-slot:item.photoLink="{ item }">
              <v-img v-if="item.photoLink" :src="item.photoLink" width="44" height="44" contain class="my-1 mx-auto"></v-img>
            </template>
            <template v-slot:item.productName="{ item }">
              <div>{{ item.productName }}</div>
              <div style="font-size: 12px; color: #9e9e9e;">{{ item.sku }}<span v-if="item.warehouseName"> · {{ item.warehouseName }}</span></div>
            </template>
            <template v-slot:item.stockQty="{ item }">{{ formatMoney(String(item.stockQty)) }}</template>
            <template v-slot:item.allocatedQty="{ item }">{{ formatMoney(String(item.allocatedQty)) }}</template>
            <template v-slot:item.unallocatedQty="{ item }">
              <span v-if="item.isOverAllocated" style="color: red;">
                <b>{{ use_language.over_allocated }} {{ formatMoney(String(item.overAllocatedQty)) }}</b>
              </span>
              <span v-else>{{ formatMoney(String(item.unallocatedQty)) }}</span>
            </template>
          </v-data-table>

          <div class="pt-3">
            <v-pagination
              v-if="summaryPageCount > 1"
              v-model="summary.page"
              :length="summaryPageCount"
              :total-visible="7"
              @input="loadSummary()"
            ></v-pagination>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  import Swal from 'sweetalert2'
  import Loading from '@/website/components/Loading'
  import OmsImportDialog from '@/website/backend/oms/components/OmsImportDialog'
  import { formatMoney } from '@/website/global_function'
  import { getOmsLanguage } from '@/website/backend/oms/oms_language'
  import { getQuotaLocationList, getQuotaList, saveQuota, importQuota, getQuotaSummary } from '@/website/backend/oms/oms_service'

  export default {
    components: {
      Loading,
      OmsImportDialog
    },
    data: () => ({
      // page
      page_loading: true,
      loading: false,
      use_language: getOmsLanguage(),
      tab: 0,
      importDialog: false,
      overAllocatedCount: 0,

      // ชั้นที่ 1: รายการ location
      locations: [],
      locationFilter: { channel: null, keyword: null },

      // ชั้นที่ 2: สินค้าของ location ที่เลือก (current = null คืออยู่ที่รายการ location)
      current: null,
      quota: { keyword: null, onlyAllocated: false, page: 1, take: 50, count: 0, items: [] },
      edits: {},     // stockShopID -> โควตาใหม่ที่ยังไม่บันทึก (คงอยู่ข้ามหน้าของ location เดียวกัน)
      problems: {},  // stockShopID -> ข้อความที่ backend ไม่ให้ผ่าน

      // สรุปเทียบ stock
      summary: { keyword: null, onlyOverAllocated: false, page: 1, take: 50, count: 0, items: [], isLoaded: false }
    }),
    computed: {
      editCount () {
        return Object.keys(this.edits).length
      },
      quotaPageCount () {
        return Math.ceil(this.quota.count / this.quota.take)
      },
      summaryPageCount () {
        return Math.ceil(this.summary.count / this.summary.take)
      },
      channelItems () {
        return [
          { value: null, text: this.use_language.all },
          { value: 'ON', text: 'ON' },
          { value: 'TT', text: 'TT' }
        ]
      },
      filteredLocations () {
        const keyword = (this.locationFilter.keyword || '').trim().toLowerCase()
        return this.locations.filter(item =>
          (this.locationFilter.channel == null || item.channel == this.locationFilter.channel) &&
          (keyword == '' || item.locationName.toLowerCase().includes(keyword) || (item.code || '').toLowerCase().includes(keyword))
        )
      },
      locationHeader () {
        return [
          { text: this.use_language.no, align: 'center', value: 'no', sortable: false, width: '70px' },
          { text: this.use_language.channel, align: 'center', value: 'channel', sortable: false, width: '100px' },
          { text: this.use_language.code, align: 'center', value: 'code', sortable: false, width: '110px' },
          { text: this.use_language.name, align: 'left', value: 'locationName', sortable: false },
          { text: this.use_language.product_count, align: 'right', value: 'productCount', sortable: true, width: '150px' },
          { text: this.use_language.quota_total, align: 'right', value: 'quotaQty', sortable: true, width: '120px' },
          { text: this.use_language.reserved_qty, align: 'right', value: 'reservedQty', sortable: true, width: '110px' },
          { text: this.use_language.used_qty, align: 'right', value: 'usedQty', sortable: true, width: '110px' },
          { text: this.use_language.available_qty, align: 'right', value: 'availableQty', sortable: true, width: '110px' },
          { text: this.use_language.edit, align: 'center', value: 'detail', sortable: false, width: '80px' }
        ]
      },
      quotaHeader () {
        return [
          { text: '', align: 'center', value: 'photoLink', sortable: false, width: '70px' },
          { text: this.use_language.product, align: 'left', value: 'productName', sortable: false },
          { text: this.use_language.stock_qty, align: 'right', value: 'stockQty', sortable: false, width: '120px' },
          { text: this.use_language.unallocated_qty, align: 'right', value: 'unallocatedQty', sortable: false, width: '130px' },
          { text: this.use_language.quota_qty, align: 'center', value: 'quotaQty', sortable: false, width: '150px' },
          { text: this.use_language.reserved_qty, align: 'right', value: 'reservedQty', sortable: false, width: '110px' },
          { text: this.use_language.used_qty, align: 'right', value: 'usedQty', sortable: false, width: '110px' },
          { text: this.use_language.available_qty, align: 'right', value: 'availableQty', sortable: false, width: '110px' }
        ]
      },
      summaryHeader () {
        return [
          { text: '', align: 'center', value: 'photoLink', sortable: false, width: '70px' },
          { text: this.use_language.product, align: 'left', value: 'productName', sortable: false },
          { text: this.use_language.stock_qty, align: 'right', value: 'stockQty', sortable: false, width: '150px' },
          { text: this.use_language.allocated_qty, align: 'right', value: 'allocatedQty', sortable: false, width: '190px' },
          { text: this.use_language.unallocated_qty, align: 'right', value: 'unallocatedQty', sortable: false, width: '170px' }
        ]
      },
      importColumns () {
        return [
          { key: 'channel', header: 'ช่องทาง', aliases: ['Channel'] },
          { key: 'locationName', header: 'Location', aliases: ['Storage Location', 'ชื่อ Location'] },
          { key: 'sku', header: 'SKU', aliases: ['รหัสสินค้า'] },
          { key: 'quotaQty', header: 'โควตา', aliases: ['Quota', 'จำนวน'], type: 'number' }
        ]
      },
      importTemplateRows () {
        return [
          ['ON', 'จังหวัดนนทบุรี (1)', 'POSM-TISSUE-01', 500],
          ['TT', '1017 Pull', 'POSM-TISSUE-01', 200]
        ]
      }
    },
    async created () {
      document.title = this.$router.history.current.meta.title
      await this.loadLocations()
      await this.loadOverAllocatedCount()
      this.page_loading = false
    },
    methods: {
      formatMoney,
      importQuota,
      showError (response) {
        this.$swal({
          position: 'top',
          type: 'error',
          text: response.message || (response.errorCode == 'NETWORK' ? this.use_language.network_error : this.use_language.cannot_perform_operation)
        })
      },
      async confirmDiscard () {
        if (this.editCount == 0) {
          return true
        }
        const result = await Swal.fire({
          type: 'question',
          text: this.use_language.unsaved_changes,
          confirmButtonText: this.use_language.confirm,
          cancelButtonText: this.use_language.cancel,
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          allowOutsideClick: false
        })
        return !!result.value
      },

      // ---------- ชั้นที่ 1: รายการ location ----------
      async loadLocations () {
        this.loading = true
        const response = await getQuotaLocationList()
        this.loading = false
        if (response.status != 'success') {
          this.showError(response)
          return
        }
        this.locations = response.items
      },
      async openLocation (location) {
        this.current = location
        this.edits = {}
        this.problems = {}
        this.quota.keyword = null
        this.quota.onlyAllocated = false
        this.quota.page = 1
        this.quota.items = []
        this.quota.count = 0
        await this.loadQuota()
      },
      async backToLocations () {
        if (!(await this.confirmDiscard())) {
          return
        }
        this.current = null
        this.edits = {}
        this.problems = {}
        // ยอดรวมของ location อาจเปลี่ยนจากการบันทึกเมื่อครู่
        await this.loadLocations()
      },

      // ---------- ชั้นที่ 2: สินค้าของ location ----------
      async searchQuota () {
        this.quota.page = 1
        await this.loadQuota()
      },
      async loadQuota () {
        if (this.current == null) {
          return
        }
        this.loading = true
        const response = await getQuotaList({
          locationIds: [this.current.locationID],
          keyword: this.quota.keyword,
          onlyAllocated: this.quota.onlyAllocated,
          skip: (this.quota.page - 1) * this.quota.take,
          take: this.quota.take
        })
        this.loading = false
        if (response.status != 'success') {
          this.showError(response)
          return
        }
        this.quota.items = response.items
        this.quota.count = response.searchResultCount
      },
      setEdit (item, value) {
        this.$delete(this.problems, item.stockShopID)
        const number = value === '' || value == null ? null : Math.floor(Number(value))
        if (number == null || isNaN(number) || number == item.quotaQty) {
          this.$delete(this.edits, item.stockShopID)
        } else {
          this.$set(this.edits, item.stockShopID, number)
        }
      },
      unallocatedOf (item) {
        const quotaQty = this.edits[item.stockShopID] != null ? this.edits[item.stockShopID] : item.quotaQty
        return item.unallocatedQty - (quotaQty - item.quotaQty)
      },
      availableOf (item) {
        const quotaQty = this.edits[item.stockShopID] != null ? this.edits[item.stockShopID] : item.quotaQty
        return quotaQty - item.reservedQty - item.usedQty
      },
      async SaveQuota () {
        const items = Object.keys(this.edits).map(stockShopId => ({ stockShopId: Number(stockShopId), quotaQty: this.edits[stockShopId] }))
        if (items.some(item => item.quotaQty < 0)) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.quota_qty + ' ' + this.use_language.quota_must_not_negative })
          return
        }
        this.loading = true
        const response = await saveQuota({ locationId: this.current.locationID, items: items })
        this.loading = false
        if (response.status != 'success') {
          // backend ไม่บันทึกเลยทั้งชุด และบอกรายการที่ไม่ผ่านพร้อมเหตุผล
          const problems = {}
          for (const problem of response.items || []) {
            problems[problem.stockShopID] = problem.message
          }
          this.problems = problems
          this.showError(response)
          return
        }
        this.edits = {}
        this.problems = {}
        await this.loadQuota()
        await this.loadOverAllocatedCount()
        this.summary.isLoaded = false
        this.$swal({ toast: true, timer: 1500, showConfirmButton: false, position: 'top', type: 'success', title: this.use_language.success })
      },
      async afterImport () {
        this.edits = {}
        this.problems = {}
        this.summary.isLoaded = false
        if (this.current != null) {
          await this.loadQuota()
        } else {
          await this.loadLocations()
        }
        await this.loadOverAllocatedCount()
      },

      // ---------- สรุปเทียบ stock ----------
      async onTabChange () {
        if (this.tab == 1 && !this.summary.isLoaded) {
          await this.loadSummary()
        }
      },
      async showOverAllocated () {
        this.tab = 1
        this.summary.onlyOverAllocated = true
        await this.searchSummary()
      },
      async searchSummary () {
        this.summary.page = 1
        await this.loadSummary()
      },
      async loadSummary () {
        this.loading = true
        const response = await getQuotaSummary({
          keyword: this.summary.keyword,
          onlyOverAllocated: this.summary.onlyOverAllocated,
          skip: (this.summary.page - 1) * this.summary.take,
          take: this.summary.take
        })
        this.loading = false
        if (response.status != 'success') {
          this.showError(response)
          return
        }
        this.summary.items = response.items
        this.summary.count = response.searchResultCount
        this.summary.isLoaded = true
        this.overAllocatedCount = response.overAllocatedCount
      },
      async loadOverAllocatedCount () {
        const response = await getQuotaSummary({ skip: 0, take: 1 })
        if (response.status == 'success') {
          this.overAllocatedCount = response.overAllocatedCount
        }
      }
    }
  }
</script>
